import { Avatar, Box, Flex, IconButton, Text, Tooltip } from "@chakra-ui/react";
import { useLocation } from "react-router";
import { useNavigate } from "react-router-dom";
import i18n from "../locales/i18n";
import { MenuList } from "../utils/menuItem";
import { FaLanguage } from "react-icons/fa";
import { BiLogOutCircle } from "react-icons/bi";
import { HiMenuAlt2 } from "react-icons/hi";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getMyProfile } from "../http/users";
import _intersection from "lodash/intersection";

// ─── Design Tokens (DESIGN.md compliant) ──────────────────────────────────────
const SIDEBAR_BG        = "#1459b8";           // Brand primary (DESIGN.md) - darkened
const SIDEBAR_DARK      = "#1560c8";           // Slightly darker shade for depth
const ACTIVE_BG         = "rgba(255,255,255,0.18)";
const ACTIVE_BORDER     = "#ffffff";
const HOVER_BG          = "rgba(255,255,255,0.10)";
const TEXT_PRIMARY      = "#ffffff";
const TEXT_MUTED        = "rgba(255,255,255,0.60)";
const DIVIDER           = "rgba(255,255,255,0.12)";
const PROFILE_CARD_BG   = "rgba(255,255,255,0.10)";
const PROFILE_BORDER    = "rgba(255,255,255,0.18)";
const LOGOUT_HOVER_BG   = "rgba(255,59,48,0.18)";   // System Red tint (DESIGN.md)
const LOGOUT_HOVER_TEXT = "#ff6b6b";

interface MenuComponentProps {
    isCollapsed?: boolean;
    onToggleCollapse?: () => void;
}

export default function MenuComponent({ isCollapsed = false, onToggleCollapse }: MenuComponentProps) {
    const { t } = useTranslation('common');
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [lang, setLang] = useState<string>('en');
    const [user, setUser] = useState<any>(undefined);

    i18n.on('languageChanged', (locale) => setLang(locale));

    const onGetGreeting = () => {
        const currentHour = new Date().getHours();
        if (currentHour >= 5 && currentHour < 12) return 'Good morning';
        if (currentHour >= 12 && currentHour < 18) return 'Good afternoon';
        return 'Good evening';
    };

    useEffect(() => { onGetMyProfile(); }, []);

    const onGetMyProfile = async () => {
        try {
            const response = await getMyProfile();
            setUser(response.data);
        } catch (e) { console.log(e); }
    };

    const isActive = (path: string) => pathname === path || pathname.startsWith(path + '/');

    // ─── Collapsed Mode ──────────────────────────────────────────────────────
    if (isCollapsed) {
        return (
            <Box
                h="100vh"
                bg={SIDEBAR_BG}
                display="flex"
                flexDir="column"
                alignItems="center"
                py="16px"
                px="8px"
                borderRight={`1px solid ${DIVIDER}`}
            >
                {/* Toggle */}
                {onToggleCollapse && (
                    <Tooltip label="Expand sidebar" placement="right">
                        <IconButton
                            aria-label="Expand sidebar"
                            icon={<HiMenuAlt2 size={18} />}
                            onClick={onToggleCollapse}
                            variant="ghost"
                            color={TEXT_MUTED}
                            _hover={{ bg: HOVER_BG, color: TEXT_PRIMARY }}
                            size="sm"
                            mb="20px"
                        />
                    </Tooltip>
                )}

                {/* Brand mark */}
                <Box
                    w="34px" h="34px"
                    borderRadius="9px"
                    bg="rgba(255,255,255,0.20)"
                    border={`1px solid rgba(255,255,255,0.30)`}
                    display="flex" alignItems="center" justifyContent="center"
                    mb="24px"
                >
                    <Text fontSize="14px" fontWeight={900} color="white">I</Text>
                </Box>

                {/* Avatar */}
                {user && (
                    <Tooltip label={user.displayName} placement="right">
                        <Box mb="20px" cursor="pointer" onClick={() => navigate("/user/" + user.id, { state: user })}>
                            <Avatar
                                name={user.displayName}
                                src={user.image}
                                size="sm"
                                border="2px solid rgba(255,255,255,0.50)"
                            />
                        </Box>
                    </Tooltip>
                )}

                {/* Divider */}
                <Box w="28px" h="1px" bg={DIVIDER} mb="12px" />

                {/* Nav Icons */}
                <Box flex="1" display="flex" flexDir="column" alignItems="center" gap="4px" w="100%" overflowY="auto"
                     css={{
                         '&::-webkit-scrollbar': { width: '0px' },
                     }}
                >
                    {[
                        { title: 'Main Link', labels: ['Dashboard', 'Student', 'My_Students'] },
                        { title: 'Reporting', labels: ['Student Progress', 'Objectives Progress', 'IEP Report'] },
                        { title: 'Entity Management', labels: ['User', 'School'] }
                    ].map((section, index, arr) => {
                        const items = section.labels.map(l => MenuList.find(m => m.label === l)).filter(Boolean) as any[];
                        const accessibleItems = items.filter(menu => user && _intersection(user.roles, menu.accessTo).length > 0);
                        if (accessibleItems.length === 0) return null;

                        return (
                            <Box key={section.title} w="100%" display="flex" flexDir="column" alignItems="center" gap="4px" mb="12px">
                                {accessibleItems.map((menu) => (
                                    <Tooltip key={menu.path} label={t(`${menu.label}`)} placement="right">
                                        <IconButton
                                            aria-label={t(`${menu.label}`)}
                                            icon={<menu.icon size={18} />}
                                            onClick={() => navigate(menu.path)}
                                            variant="ghost"
                                            w="40px" h="40px"
                                            borderRadius="10px"
                                            color={TEXT_PRIMARY}
                                            bg={isActive(menu.path) ? ACTIVE_BG : "transparent"}
                                            _hover={{ bg: HOVER_BG, color: TEXT_PRIMARY }}
                                            transition="all 0.18s ease"
                                        />
                                    </Tooltip>
                                ))}
                                {index < arr.length - 1 && <Box w="28px" h="1px" bg={DIVIDER} mt="8px" />}
                            </Box>
                        );
                    })}
                </Box>

                {/* Bottom utilities */}
                <Box display="flex" flexDir="column" alignItems="center" gap="4px" pt="12px" borderTop={`1px solid ${DIVIDER}`} w="100%">
                    <Tooltip label={lang === 'ne' ? 'Change Language' : 'भाषा परिवर्तन'} placement="right">
                        <IconButton
                            aria-label="Change language"
                            icon={<FaLanguage size={18} />}
                            onClick={() => i18n.changeLanguage(i18n.language === 'ne' ? 'en' : 'ne')}
                            variant="ghost"
                            color={TEXT_PRIMARY}
                            _hover={{ bg: HOVER_BG, color: TEXT_PRIMARY }}
                            size="sm"
                        />
                    </Tooltip>
                    <Tooltip label={t('logout')} placement="right">
                        <IconButton
                            aria-label={t('logout')}
                            icon={<BiLogOutCircle size={18} />}
                            onClick={() => { localStorage.clear(); window.location.reload(); }}
                            variant="ghost"
                            color={TEXT_PRIMARY}
                            _hover={{ bg: LOGOUT_HOVER_BG, color: LOGOUT_HOVER_TEXT }}
                            size="sm"
                        />
                    </Tooltip>
                </Box>
            </Box>
        );
    }

    // ─── Expanded Mode ───────────────────────────────────────────────────────
    return (
        <Box
            h="100vh"
            bg={SIDEBAR_BG}
            display="flex"
            flexDir="column"
            borderRight={`1px solid ${DIVIDER}`}
            overflow="hidden"
        >
            {/* ── Header ── */}
            <Box px="18px" pt="20px" pb="8px">
                <Flex align="center" justify="space-between">
                    <Flex align="center" gap="10px">
                        {/* Logo mark */}
                        <Box
                            w="32px" h="32px"
                            borderRadius="8px"
                            bg="rgba(255,255,255,0.20)"
                            border="1px solid rgba(255,255,255,0.30)"
                            display="flex" alignItems="center" justifyContent="center"
                            flexShrink={0}
                        >
                            <Text fontSize="14px" fontWeight={900} color="white" lineHeight={1}>I</Text>
                        </Box>
                        <Box>
                            <Text
                                fontSize="15px"
                                fontWeight={700}
                                color={TEXT_PRIMARY}
                                lineHeight={1.2}
                                letterSpacing="0.01em"
                            >
                                {t('mainName')}
                            </Text>
                            <Text
                                fontSize="10px"
                                color={TEXT_MUTED}
                                letterSpacing="0.08em"
                                textTransform="uppercase"
                                mt="1px"
                            >
                                Management
                            </Text>
                        </Box>
                    </Flex>

                    {onToggleCollapse && (
                        <Tooltip label="Collapse sidebar" placement="right">
                            <IconButton
                                aria-label="Collapse sidebar"
                                icon={<HiMenuAlt2 size={18} />}
                                onClick={onToggleCollapse}
                                variant="ghost"
                                color={TEXT_MUTED}
                                _hover={{ bg: HOVER_BG, color: TEXT_PRIMARY }}
                                size="sm"
                            />
                        </Tooltip>
                    )}
                </Flex>
            </Box>

            {/* ── Profile Card ── */}
            {user && (
                <Box px="12px" pt="12px" pb="4px">
                    <Box
                        bg={PROFILE_CARD_BG}
                        border={`1px solid ${PROFILE_BORDER}`}
                        borderRadius="12px"
                        p="12px"
                        cursor="pointer"
                        onClick={() => navigate("/user/" + user.id, { state: user })}
                        _hover={{ bg: "rgba(255,255,255,0.15)", borderColor: "rgba(255,255,255,0.30)" }}
                        transition="all 0.18s ease"
                        backdropFilter="blur(10px)"
                    >
                        <Flex align="center" gap="10px">
                            <Avatar
                                name={user.displayName}
                                src={user.image}
                                size="sm"
                                border="2px solid rgba(255,255,255,0.50)"
                                flexShrink={0}
                            />
                            <Box overflow="hidden">
                                <Text
                                    fontSize="11px"
                                    color={TEXT_MUTED}
                                    lineHeight={1.2}
                                    noOfLines={1}
                                >
                                    {onGetGreeting()}
                                </Text>
                                <Text
                                    fontSize="15px"
                                    fontWeight={600}
                                    color={TEXT_PRIMARY}
                                    lineHeight={1.3}
                                    noOfLines={1}
                                >
                                    {user.displayName}
                                </Text>
                            </Box>
                        </Flex>
                    </Box>
                </Box>
            )}

            {/* ── Nav Items ── */}
            <Box
                flex="1"
                overflowY="auto"
                px="10px"
                pt="20px"
                pb="8px"
                css={{
                    '&::-webkit-scrollbar': { width: '3px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.20)', borderRadius: '10px' },
                }}
            >
                {[
                    { title: 'Main Link', labels: ['Dashboard', 'Student', 'My_Students'] },
                    { title: 'Reporting', labels: ['Student Progress', 'Objectives Progress', 'IEP Report'] },
                    { title: 'Entity Management', labels: ['User', 'School'] }
                ].map((section, index, arr) => {
                    const items = section.labels.map(l => MenuList.find(m => m.label === l)).filter(Boolean) as any[];
                    const accessibleItems = items.filter(menu => user && _intersection(user.roles, menu.accessTo).length > 0);
                    if (accessibleItems.length === 0) return null;

                    return (
                        <Box key={section.title} mb="12px">
                            <Box px="10px" pt="4px" pb="6px">
                                <Text
                                    fontSize="10px"
                                    fontWeight={600}
                                    color={TEXT_MUTED}
                                    letterSpacing="0.10em"
                                    textTransform="uppercase"
                                >
                                    {section.title}
                                </Text>
                            </Box>
                            {accessibleItems.map((menu) => (
                                <Box key={menu.path} position="relative" mb="2px">
                                    {/* Active left bar */}
                                    {isActive(menu.path) && (
                                        <Box
                                            position="absolute"
                                            left="0"
                                            top="50%"
                                            transform="translateY(-50%)"
                                            w="3px"
                                            h="60%"
                                            bg={ACTIVE_BORDER}
                                            borderRadius="0 3px 3px 0"
                                            zIndex={1}
                                        />
                                    )}
                                    <Flex
                                        as="button"
                                        align="center"
                                        gap="12px"
                                        px="14px"
                                        py="10px"
                                        borderRadius="10px"
                                        w="100%"
                                        cursor="pointer"
                                        bg={isActive(menu.path) ? ACTIVE_BG : "transparent"}
                                        color={TEXT_PRIMARY}
                                        _hover={{ bg: isActive(menu.path) ? ACTIVE_BG : HOVER_BG, color: TEXT_PRIMARY }}
                                        transition="all 0.18s ease"
                                        onClick={() => navigate(menu.path)}
                                        border="none"
                                        outline="none"
                                        textAlign="left"
                                    >
                                        <Box
                                            color="inherit"
                                            transition="color 0.18s ease"
                                            display="flex"
                                            alignItems="center"
                                            flexShrink={0}
                                        >
                                            <menu.icon size={19} />
                                        </Box>
                                        <Text
                                            fontSize="15px"
                                            fontWeight={isActive(menu.path) ? 600 : 400}
                                            letterSpacing="0.01em"
                                            noOfLines={1}
                                            flex="1"
                                        >
                                            {t(`${menu.label}`)}
                                        </Text>
                                    </Flex>
                                </Box>
                            ))}
                            {index < arr.length - 1 && <Box w="85%" mx="auto" h="1px" bg={DIVIDER} mt="12px" mb="4px" />}
                        </Box>
                    );
                })}
            </Box>

            {/* ── Bottom Utilities ── */}
            <Box
                px="10px"
                pt="8px"
                pb="16px"
                borderTop={`1px solid ${DIVIDER}`}
                mt="auto"
            >
                {/* Language toggle */}
                <Flex
                    as="button"
                    align="center"
                    gap="12px"
                    px="14px"
                    py="9px"
                    borderRadius="10px"
                    w="100%"
                    cursor="pointer"
                    color={TEXT_PRIMARY}
                    _hover={{ bg: HOVER_BG, color: TEXT_PRIMARY }}
                    transition="all 0.18s ease"
                    onClick={() => i18n.changeLanguage(i18n.language === 'ne' ? 'en' : 'ne')}
                    border="none"
                    outline="none"
                    mb="2px"
                >
                    <FaLanguage size={17} />
                    <Text fontSize="13.5px" fontWeight={400} letterSpacing="0.01em">
                        {lang === 'ne' ? 'Change Language' : 'भाषा परिवर्तन'}
                    </Text>
                </Flex>

                {/* Logout */}
                <Flex
                    as="button"
                    align="center"
                    gap="12px"
                    px="14px"
                    py="9px"
                    borderRadius="10px"
                    w="100%"
                    cursor="pointer"
                    color={TEXT_PRIMARY}
                    _hover={{ bg: LOGOUT_HOVER_BG, color: LOGOUT_HOVER_TEXT }}
                    transition="all 0.18s ease"
                    onClick={() => { localStorage.clear(); window.location.reload(); }}
                    border="none"
                    outline="none"
                >
                    <BiLogOutCircle size={17} />
                    <Text fontSize="13.5px" fontWeight={400} letterSpacing="0.01em">
                        {t('logout')}
                    </Text>
                </Flex>
            </Box>
        </Box>
    );
}
